// normalizeFlightTimestamps — pure per-flight departure/arrival epoch conversion.
//
// Extracted VERBATIM from `components/ManualTripCreator.js` (the
// `flightsWithTimestamps` mapping inside `buildTripData`) as part of the
// behavior-preserving refactor (issue #240). For each flight it returns a
// shallow copy with `departureTime` / `arrivalTime` converted to epoch
// milliseconds WHEN they are strings, and passed through unchanged otherwise
// (already-numeric epochs, or undefined). Every other field on the flight is
// spread through untouched, preserving the exact shape appended by the
// added-flights list.
//
// Totality mirrors the source EXACTLY:
//   - The per-field test is `typeof flight.departureTime === 'string' ? new
//     Date(flight.departureTime).getTime() : flight.departureTime`, copied
//     verbatim (and likewise for `arrivalTime`). The source did NOT guard the
//     `flights` argument or individual flight objects beyond the `typeof`
//     string check, so NO extra guard is added.
//   - `new Date(<unparseable string>).getTime()` yields `NaN` here too; the
//     source added no guard against that, so none is added.
//   - Reads NO clock: each `new Date(...)` parses the flight's own absolute
//     time string, not the current time, so no `now` injection is required.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * Convert each flight's string departure/arrival times to epoch milliseconds.
 *
 * @param {Array<Object>} flights - The flights to normalize.
 * @returns {Array<Object>} Shallow-copied flights with epoch timestamps.
 */
export function normalizeFlightTimestamps(flights) {
  return flights.map(flight => ({
    ...flight,
    departureTime: typeof flight.departureTime === 'string'
      ? new Date(flight.departureTime).getTime()
      : flight.departureTime,
    arrivalTime: typeof flight.arrivalTime === 'string'
      ? new Date(flight.arrivalTime).getTime()
      : flight.arrivalTime
  }));
}

export default normalizeFlightTimestamps;
